import React from "react";
import { Tooltip } from "@mui/material";
import { League, LeagueGame, Player, PlayerGameScore, RosterPositionEnum, ScoringSettings, Team, TeamPlayerInfo, NflGame } from "../../sdk/model";
import { getPlayerCurrentScoreFromStats } from "./ScoreService";
import { Box } from "@mui/system";

export function loadGameRosterFromQuickstats(league: League, game: LeagueGame, playerMap : Map<string | undefined, Player | undefined>, nflGameMap: Map<string, NflGame>) : LeagueGame {
  console.debug("Loading game roster from quickstats...")

  // Have to do a deep copy so the game actually changes
  const newGame = {...game};
  const homeTeam: Team = league?.teams.find(team => team.id === game.homeTeamId)!;
  const homeTeamRoster = homeTeam ? homeTeam.playerRoster : {};
  newGame.homeTeamRoster = getGameRoster(homeTeamRoster, league, playerMap, nflGameMap);

  const awayTeam: Team = league?.teams.find(team => team.id === game.awayTeamId)!;
  const awayTeamRoster = awayTeam ? awayTeam.playerRoster : {};
  newGame.awayTeamRoster = getGameRoster(awayTeamRoster, league, playerMap, nflGameMap);
  
  if (!game.finalScore) {
    newGame.homeTeamScore =  undefined
    newGame.awayTeamScore = undefined
  }

  scoreGame(newGame);

  return newGame;
}

function getSingularUnit(statType: string): string {
  const lastWord = statType.split(' ').pop()?.toLowerCase() || '';
  
  // Handle common pluralization patterns
  const singularMap: { [key: string]: string } = {
    'yards': 'yard',
    'receptions': 'reception',
    'interceptions': 'interception',
    'conversions': 'conversion',
    'sacks': 'sack',
    'downs': 'down',
    'fumbles': 'fumble',
    'tds': 'td',
    'touchdowns': 'touchdown'
  };
  
  return singularMap[lastWord] || lastWord;
}

export function ScoreToolTip({ playerGameScore, children }: { playerGameScore: PlayerGameScore, children: React.ReactNode }) {
  const formatScoreBreakdown = () => {
    if (playerGameScore.totalScore === 0) {
      return (
        <Box sx={{ 
          maxWidth: 300, 
          backgroundColor: '#1565c0', 
          padding: 2, 
          borderRadius: 1,
          textAlign: 'center',
          color: 'white'
        }}>
          <Box sx={{ fontSize: '1.2em', marginBottom: 1 }}>
            Big donut! 🍩
          </Box>
          <Box sx={{ fontSize: '0.9em', color: 'rgba(255,255,255,0.7)' }}>
            No points scored yet
          </Box>
        </Box>
      );
    }
    
    if (!playerGameScore.scoreBreakdown || playerGameScore.scoreBreakdown.length === 0) {
      return (
        <Box sx={{ 
          maxWidth: 300, 
          backgroundColor: '#1565c0', 
          padding: 2, 
          borderRadius: 1,
          color: 'white'
        }}>
          No score breakdown available
        </Box>
      );
    }
    
    return (
      <Box sx={{ 
        maxWidth: 300, 
        backgroundColor: '#1565c0', 
        padding: 2, 
        borderRadius: 1,
        color: 'white'
      }}>
        <Box sx={{ fontWeight: 'bold', marginBottom: 1 }}>
          Score Breakdown ({playerGameScore.totalScore} pts)
        </Box>
        {playerGameScore.scoreBreakdown.map((breakdown, index) => (
          <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 0.5 }}>
            <Box sx={{ flex: 1 }}>
              {breakdown.statType}: {breakdown.stat}
              {breakdown.pointsPer && (
                <Box component="span" sx={{ fontSize: '0.8em', color: 'rgba(255,255,255,0.7)', marginLeft: 1 }}>
                  ({breakdown.pointsPer} pts per {getSingularUnit(breakdown.statType || '')})
                </Box>
              )}
            </Box>
            <Box sx={{ fontWeight: 'bold', marginLeft: 2 }}>
              {breakdown.points} pts
            </Box>
          </Box>
        ))}
      </Box>
    );
  };
  
  return (
    <Tooltip 
      title={formatScoreBreakdown()}
      enterTouchDelay={0}
      leaveTouchDelay={1500}
      enterDelay={0}
      leaveDelay={200}
      disableInteractive={true}
    >
      <Box>{children}</Box>
    </Tooltip>
  );
}

/**
 * This only adds up the scores from the starters to get the total score for the team
 */
export function scoreGame(game: LeagueGame) {
  if (!game.homeTeamScore) {
    game.homeTeamScore = scoreRoster(game.homeTeamRoster, player => player.totalScore) + (game.homeTeamScoreAdjustment || 0);
    game.homeTeamProjection = scoreRoster(game.homeTeamRoster, player => player.projectedScore) + (game.homeTeamScoreAdjustment || 0);
  }
  if (!game.awayTeamScore) {
    game.awayTeamScore = scoreRoster(game.awayTeamRoster, player => player.totalScore) + (game.awayTeamScoreAdjustment || 0);
    game.awayTeamProjection = scoreRoster(game.awayTeamRoster, player => player.projectedScore) + (game.awayTeamScoreAdjustment || 0);
  }
  game.winningTeamId = game.homeTeamScore > game.awayTeamScore ? game.homeTeamId : game.awayTeamId;
  game.gameScored = true;
}

function scoreRoster(teamRoster: { [key: string]: PlayerGameScore[]; } | undefined, scoreFunction : Function) {
  if(!teamRoster) {
    return 0;
  }
  let score = 0;
  // TODO: Probably should make this logic a little better and more consistent with the way the backend does it
  Object.keys(teamRoster).forEach((position) => {
    if(position === RosterPositionEnum.Bn || position === RosterPositionEnum.Ir || position === RosterPositionEnum.Ps) {
      return;
    }
    const players = teamRoster[position];
    players.forEach(player => {
      score += scoreFunction(player) || 0;
    });
  });
  return parseFloat(score.toFixed(2));
  
}



function getGameRoster(teamRoster: { [key: string]: TeamPlayerInfo[]; } | undefined, league: League,
      playerMap : Map<string | undefined, Player | undefined>, nflGameMap: Map<string, NflGame>): { [key: string]: PlayerGameScore[]; } | undefined {
  if(!teamRoster) {
    return undefined;
  }
  const gameRoster: { [key: string]: PlayerGameScore[]; } = {};
  Object.keys(teamRoster).forEach((position) => {
    const players = teamRoster[position];
    gameRoster[position] = players.map(player => {
      return playerToScore(player, league?.currentSeason?.scoringSettings!, playerMap, nflGameMap);
    });
  });
  return gameRoster;
}

function playerToScore(playerInfo: TeamPlayerInfo, scoringSettings: ScoringSettings, playerMap : Map<string | undefined, Player | undefined>, nflGameMap: Map<string, NflGame>): any {
  if(!playerInfo) {
    return {};
  }
  const player: Player = playerMap.get(playerInfo?.playerId)!;
  if(!player) {
    // TODO: If we start retiring players, we should handle this better
    console.error("Player not found for id: " + playerInfo?.playerId);
    return {};
  }
  return getPlayerCurrentScoreFromStats(player, scoringSettings, nflGameMap);
}